import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { SuggestionLinkTemplate, ProductCardTemplate, CollectionLinkTemplate, BlogLinkTemplate, getBlogUrl } from './templates';
import { fetchTypingStateData } from './composables/useSearchData';
import { setAlgoliaSearchAttribution } from '../../search/algolia/attribution';

export const getRecentSearchesPlugin = (getAutocompleteInstance, closeFn) => {
  return createLocalStorageRecentSearchesPlugin({
    key: 'stio-recent-searches',
    limit: 5,
    transformSource({ source, state }) {
      // Hide recent searches when user is typing (match Vuori behavior)
      if (state.query) return undefined;

      return {
        ...source,
        sourceId: 'recentSearches',
        templates: {
          header({ html }) {
            return html`
              <div class="aa-SectionTitle aa-SectionTitle--clearable">
                <span>Recent Searches</span>
                <button
                  class="aa-SectionTitle__clear"
                  onClick=${(e) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.removeItem('AUTOCOMPLETE_RECENT_SEARCHES:stio-recent-searches');
    const instance = getAutocompleteInstance();
    if (instance) {
      instance.refresh();
    }
  }}
                >Clear</button>
              </div>
            `;
          },
          item({ item, html }) {
            const label = item.label || item.id || '';
            return SuggestionLinkTemplate(html, label);
          },
          noResults() {
            return null;
          },
        },
        onSelect({ item, setQuery }) {
          const label = item.label || item.id || '';
          setQuery(label);
          window.location.href = `/search?q=${encodeURIComponent(label)}`;
          closeFn();
        },
      };
    },
  });
};

export const getEmptyStateSources = (getTrendingItems, getPopularItems, closeFn) => {
  return [
    {
      sourceId: 'trending',
      getItems() {
        return getTrendingItems();
      },
      onSelect({ item }) {
        const label = item.query || item.title || item.name || '';
        window.location.href = `/search?q=${encodeURIComponent(label)}`;
        closeFn();
      },
      templates: {
        header({ html }) {
          return html`<div class="aa-SectionTitle">Trending</div>`;
        },
        item({ item, html }) {
          const label = item.query || item.title || item.name || '';
          return SuggestionLinkTemplate(html, label);
        },
      },
    },
    {
      sourceId: 'popular',
      getItems() {
        return getPopularItems();
      },
      onSelect({ item }) {
        setAlgoliaSearchAttribution(item);
        window.location.href = `/products/${item.handle}`;
        closeFn();
      },
      templates: {
        header({ html }) {
          return html`<div class="aa-SectionTitle">Popular</div>`;
        },
        item({ item, html }) {
          return ProductCardTemplate(html, item);
        },
      },
    },
  ];
};

const interleave = (a, b) => {
  const result = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    if (i < a.length) result.push(a[i]);
    if (i < b.length) result.push(b[i]);
  }
  return result;
};

export const getTypingStateSources = (query, closeFn, getNbHits, setNbHits) => {
  return [
    {
      sourceId: 'querySuggestions',
      getItems() {
        return fetchTypingStateData(query).then(({ suggestions }) => suggestions);
      },
      onSelect({ item, setQuery }) {
        const label = item.query || item.title || item.name || '';
        setQuery(label);
        window.location.href = `/search?q=${encodeURIComponent(label)}`;
        closeFn();
      },
      templates: {
        header({ html }) {
          return html`<div class="aa-SectionTitle">Search suggestions</div>`;
        },
        item({ item, html, state }) {
          const label = item.query || item.title || item.name || '';
          return SuggestionLinkTemplate(html, label, state.query);
        },
      },
    },
    {
      sourceId: 'collections',
      getItems() {
        return fetchTypingStateData(query).then(({ collections, articles }) =>
          interleave(collections, articles).slice(0, 5)
        );
      },
      onSelect({ item }) {
        if (item._itemType === 'blog') {
          window.location.href = getBlogUrl(item);
        } else {
          window.location.href = `/collections/${item.handle}`;
        }
        closeFn();
      },
      templates: {
        header({ html }) {
          return html`<div class="aa-SectionTitle">Collections & Blogs</div>`;
        },
        item({ item, html, state }) {
          return item._itemType === 'blog'
            ? BlogLinkTemplate(html, item, state.query)
            : CollectionLinkTemplate(html, item, state.query);
        },
      },
    },
    {
      sourceId: 'products',
      getItems() {
        return fetchTypingStateData(query).then(({ products }) => {
          setNbHits(products.nbHits);
          return products.productCards;
        });
      },
      onSelect({ item }) {
        setAlgoliaSearchAttribution(item);
        window.location.href = `/products/${item.handle}`;
        closeFn();
      },
      templates: {
        header({ html, state }) {
          const nbHits = getNbHits();

          return html`
            <div class="aa-SectionTitle aa-SectionTitle--clearable">
              <span>${nbHits} Results for "${state.query}"</span>
              ${nbHits > 0
                ? html`<a href=${`/search?q=${encodeURIComponent(state.query)}`} class="aa-SectionTitle__viewAll">View All Items</a>`
                : null}
            </div>
          `;
        },
        item({ item, html }) {
          return ProductCardTemplate(html, item);
        },
        noResults({ html }) {
          return html`<div class="aa-NoResults">No products found.</div>`;
        },
      },
    },
  ];
};
